import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Pagination } from 'react-bootstrap';
import { FaEye, FaEdit, FaReply, FaSearch, FaClock, FaSync, FaUser, FaUserTie } from 'react-icons/fa';
import { adminSupportService } from '../../services/adminSupportService';
import { showToast } from '../../utils/toast';
import Spinner from '../common/Spinner';

const SupportTicketsTab = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch tickets on component mount
  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await adminSupportService.getAllTickets();
      setTickets(response.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      showToast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = (ticket.request_message && ticket.request_message.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (ticket.user_email && ticket.user_email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage('');
    setShowModal(true);
  };

  const handleUpdateStatus = async (ticket, newStatus) => {
    try {
      await adminSupportService.updateTicketStatus(ticket.id, newStatus);
      showToast.success(`Ticket status updated to ${newStatus}`);
      fetchTickets(); // Refresh the list
      setCurrentPage(1); // Reset to first page
      setShowModal(false);
    } catch (error) {
      console.error('Error updating ticket status:', error);
      showToast.error('Failed to update ticket status');
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      showToast.warning('Please enter a reply message');
      return;
    }

    try {
      await adminSupportService.replyToTicket(selectedTicket.id, replyMessage);
      showToast.success('Reply sent successfully');
      setReplyMessage('');
      fetchTickets(); // Refresh the list
      setCurrentPage(1); // Reset to first page
      setShowModal(false);
    } catch (error) {
      console.error('Error sending reply:', error);
      showToast.error('Failed to send reply');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'resolved': 'success', 
      'closed': 'secondary'
    };
    const displayText = status.charAt(0).toUpperCase() + status.slice(1);
    return <Badge bg={variants[status] || 'secondary'}>{displayText}</Badge>;
  };

  const getUserName = (ticket) => {
    if (ticket.first_name && ticket.last_name) {
      return `${ticket.first_name} ${ticket.last_name}`;
    }
    return ticket.user_email || 'Unknown User';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-3">Support Tickets Management</h2>
          <p className="text-muted">View and manage customer support tickets.</p>
        </Col>
      </Row>

      {/* Search and Filter */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Search Tickets</Form.Label>
            <div className="input-group">
              <Form.Control
                type="text"
                placeholder="Search by subject or user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary">
                <FaSearch />
              </Button>
            </div>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={5} className="d-flex align-items-end">
          <Button variant="outline-primary" className="me-2" onClick={fetchTickets} disabled={loading}>
            <FaSync className="me-2" />
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Col>
      </Row>

      {/* Tickets Table */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Support Tickets ({filteredTickets.length})</h5>
            <div>
              <Badge bg="warning" className="me-2">Pending: {tickets.filter(t => t.status === 'pending').length}</Badge>
              <Badge bg="success" className="me-2">Resolved: {tickets.filter(t => t.status === 'resolved').length}</Badge>
              <Badge bg="secondary">Closed: {tickets.filter(t => t.status === 'closed').length}</Badge>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
              <p className="mt-2">Loading support tickets...</p>
            </div>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Message</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTickets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="text-muted">
                        <p>No support tickets found.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentTickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>#{ticket.id}</td>
                      <td>
                        <div style={{ maxWidth: '300px' }}>
                          <div className="text-truncate">
                            {ticket.request_message}
                          </div>
                          {ticket.response_message && (
                            <small className="text-success">
                              <FaReply className="me-1" />
                              Replied
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div>{getUserName(ticket)}</div>
                          <small className="text-muted">{ticket.user_email}</small>
                        </div>
                      </td>
                      <td>{getStatusBadge(ticket.status)}</td>
                      <td>
                        <small>{formatDate(ticket.created_at)}</small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaClock className="me-1 text-muted" />
                          <small>{formatDate(ticket.updated_at)}</small>
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-1"
                          onClick={() => handleViewTicket(ticket)}
                          title="View Details"
                        >
                          <FaEye />
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="me-1"
                          onClick={() => handleViewTicket(ticket)}
                          title="Reply"
                        >
                          <FaReply />
                        </Button>
                        {ticket.status === 'pending' && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleUpdateStatus(ticket, 'closed')}
                            title="Close Ticket"
                          >
                            <FaEdit />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
        
        {/* Pagination */}
        {!loading && filteredTickets.length > itemsPerPage && (
          <Card.Footer>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <small className="text-muted me-3">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTickets.length)} of {filteredTickets.length} tickets
                </small>
                <div className="d-flex align-items-center">
                  <small className="text-muted me-2">Show:</small>
                  <Form.Select 
                    size="sm" 
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                  <small className="text-muted ms-2">per page</small>
                </div>
              </div>
              <Pagination className="mb-0">
                <Pagination.First 
                  onClick={() => handlePageChange(1)} 
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 1}
                />
                
                {/* Page numbers */}
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  const isCurrentPage = pageNumber === currentPage;
                  
                  // Show first page, last page, current page, and pages around current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={isCurrentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  }
                  
                  // Show ellipsis
                  if (
                    (pageNumber === currentPage - 2 && currentPage > 3) ||
                    (pageNumber === currentPage + 2 && currentPage < totalPages - 2)
                  ) {
                    return <Pagination.Ellipsis key={pageNumber} disabled />;
                  }
                  
                  return null;
                })}
                
                <Pagination.Next 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                  onClick={() => handlePageChange(totalPages)} 
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* Ticket Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center">
              <FaReply className="me-2 text-primary" />
              Ticket #{selectedTicket?.id} - Support Conversation
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTicket && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Ticket Information</h6>
                  <p><strong>User:</strong> {getUserName(selectedTicket)}</p>
                  <p><strong>Email:</strong> {selectedTicket.user_email}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedTicket.status)}</p>
                </Col>
                <Col md={6}>
                  <h6>Timeline</h6>
                  <p><strong>Created:</strong> {formatDate(selectedTicket.created_at)}</p>
                  <p><strong>Last Updated:</strong> {formatDate(selectedTicket.updated_at)}</p>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col>
                  <h6>Conversation History</h6>
                  <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    {/* Customer's Initial Message */}
                    <div className="p-3 border-bottom" style={{ backgroundColor: '#ffffff' }}>
                      <div className="d-flex align-items-start">
                        <div className="me-3">
                          <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" 
                               style={{ width: '40px', height: '40px' }}>
                            <FaUser className="text-white" size={16} />
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <strong className="text-primary">{getUserName(selectedTicket)}</strong>
                              <small className="text-muted ms-2">Customer</small>
                            </div>
                            <small className="text-muted">{formatDate(selectedTicket.created_at)}</small>
                          </div>
                          <div className="bg-light p-3 rounded">
                            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.request_message}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Admin's Response (if exists) */}
                    {selectedTicket.response_message && (
                      <div className="p-3" style={{ backgroundColor: '#f0f8f0' }}>
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" 
                                 style={{ width: '40px', height: '40px' }}>
                              <FaUserTie className="text-white" size={16} />
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <strong className="text-success">Admin Support</strong>
                                <small className="text-muted ms-2">Support Team</small>
                              </div>
                              <small className="text-muted">{formatDate(selectedTicket.updated_at)}</small>
                            </div>
                            <div className="bg-white p-3 rounded border border-success border-opacity-25">
                              <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.response_message}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* No Response Yet */}
                    {!selectedTicket.response_message && (
                      <div className="p-4 text-center">
                        <div className="text-muted">
                          <FaReply size={24} className="mb-2 opacity-50" />
                          <p className="mb-0"><em>No response sent yet. Use the form below to reply to the customer.</em></p>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col>
                  <h6>
                    {selectedTicket.response_message ? 'Send New Reply' : 'Send Reply'}
                    <small className="text-muted ms-2">
                      ({selectedTicket.response_message ? 'This will update the existing response' : 'First response to this ticket'})
                    </small>
                  </h6>
                  <Form.Group>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder={selectedTicket.response_message ? 
                        "Type your updated response here..." : 
                        "Type your response here..."
                      }
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                    />
                  </Form.Group>
                  <small className="text-muted">
                    💡 Tip: Be clear and helpful in your response. The customer will see this message in their dashboard.
                  </small>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          {selectedTicket?.status === 'pending' && (
            <>
              <Button 
                variant="warning" 
                onClick={() => handleUpdateStatus(selectedTicket, 'resolved')}
                disabled={loading}
              >
                Mark Resolved
              </Button>
              <Button 
                variant="danger" 
                onClick={() => handleUpdateStatus(selectedTicket, 'closed')}
                disabled={loading}
              >
                Close Ticket
              </Button>
            </>
          )}
          <Button 
            variant="primary" 
            onClick={handleSendReply}
            disabled={!replyMessage.trim() || loading}
          >
            Send Reply
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SupportTicketsTab; 